import React, {useMemo} from 'react'
import {Trans, useTranslation} from 'react-i18next'

import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material'
import {PGButton, PGTheme, PGTypography} from '@progynyglobal/pg-ui-library'

import {useMeStore} from '@progyny/store/meStore'

type JourneyComparisonTableProps = {
  onAddJourney?: () => void
  onEndJourney?: () => void
}

const JOURNEY_RELATED_POINTS = [
  {
    fertility: [
      {
        left: [
          'Your existing journey with consultations and fertility reimbursement benefit will stay active.',
          'Keep Fertility & Family forming alongside Pregnancy & Postpartum or Menopause.',
        ],
        right: [
          'Your scheduled consultations will be canceled, and your onboarding will reset.',
          'Your fertility reimbursement benefits will remain available across journeys.',
        ],
      },
    ],
  },
]

const JourneyComparisonTable: React.FC<JourneyComparisonTableProps> = ({
  onAddJourney,
  onEndJourney,
}) => {
  const {t} = useTranslation()
  const user = useMeStore((s) => s.user)

  const journeyType = useMemo<'fertility'>(() => 'fertility', [])

  const isClaimActive =
    user?.member?.activeMemberJourney === 'fertility' &&
    user?.member?.hasOpenClaims

  const leftPoints = JOURNEY_RELATED_POINTS[0][journeyType][0].left
  const rightPoints = JOURNEY_RELATED_POINTS[0][journeyType][0].right

  return (
    <TableContainer>
      <Table>
        <TableHead sx={{backgroundColor: PGTheme.common.antiFlash}}>
          <TableRow>
            <TableCell sx={{pl: 5}}>
              <PGTypography variant='subheader'>
                {t('switch_journey_journey_comparison_table_add_journey')}
              </PGTypography>
            </TableCell>
            <TableCell sx={{pl: 5}}>
              <PGTypography variant='subheader'>
                {t('switch_journey_journey_comparison_table_end_journey')}
              </PGTypography>
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          <TableRow>
            {/* ADD JOURNEY COLUMN */}
            <TableCell sx={{verticalAlign: 'top'}}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  minHeight: 260,
                  px: 3,
                  py: 2,
                }}
              >
                <Box>
                  <ul style={{margin: 0}}>
                    {leftPoints.map((text, index) => (
                      <li key={index}>
                        <PGTypography variant='body'>
                          <Trans>{text}</Trans>
                        </PGTypography>
                      </li>
                    ))}
                  </ul>
                </Box>
                <Box sx={{flex: 1}} />

                <PGButton
                  label={t(
                    'switch_journey_journey_comparison_table_add_new_journey'
                  )}
                  value='primary'
                  size='large'
                  onClick={onAddJourney}
                  data-testid='switch-journey-add-new'
                />
              </Box>
            </TableCell>
            <TableCell sx={{verticalAlign: 'top'}}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  minHeight: 260,
                  px: 3,
                  py: 2,
                }}
              >
                <Box>
                  <ul style={{margin: 0}}>
                    {rightPoints.map((text, index) => (
                      <li key={index}>
                        <PGTypography
                          variant={
                            isClaimActive && index === 1 ? 'label' : 'body'
                          }
                        >
                          {isClaimActive && index === 1
                            ? t('switch_journey_reimbursement_progress')
                            : text}
                        </PGTypography>
                      </li>
                    ))}
                  </ul>
                </Box>

                {/* FLEX SPACER */}
                <Box sx={{flex: 1}} />

                <PGButton
                  label={t(
                    'switch_journey_journey_comparison_table_end_current_journey'
                  )}
                  value='primary'
                  size='large'
                  disabled={isClaimActive}
                  onClick={onEndJourney}
                  data-testid='switch-journey-end-current'
                />
              </Box>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export default JourneyComparisonTable
